/**
 * Share-link Firebase token helpers — pure utilities (no auth/SDK dependencies).
 *
 * Lives in `lib/` so that both `lib/firebase.ts` (token plumbing) and
 * `features/share-links/utils/share-auth-helpers.ts` (flow logic) can import
 * from a single source. Keeping this in `features/` would force `lib/firebase.ts`
 * to import downward into a feature module — backwards in dependency direction
 * and a recurring source of drift (the JWT validation rules and storage key
 * have to be edited in two places).
 */

/** sessionStorage key for the cached share-link Firebase token. */
export const SHARE_TOKEN_KEY = '___SHARE_LINK_TOKEN___';

/**
 * Reject tokens within EXPIRY_BUFFER_MS of `exp` to avoid the client/server
 * clock-skew window where the client thinks the token is valid but Firebase's
 * verifyIdToken rejects it.
 */
const EXPIRY_BUFFER_MS = 60_000;

interface FirebaseJwtPayload {
  exp?: number;
  firebase?: { sign_in_provider?: string };
  shareLinkObjectId?: string;
  [key: string]: unknown;
}

/** Decode a Firebase ID token's payload. Returns null on malformed input. */
export function decodeJwtPayload(token: string): FirebaseJwtPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    return JSON.parse(atob(parts[1])) as FirebaseJwtPayload;
  } catch {
    return null;
  }
}

/** True if `exp` is set and the token is not within EXPIRY_BUFFER_MS of expiring. */
export function isJwtNotExpired(payload: FirebaseJwtPayload): boolean {
  return typeof payload.exp === 'number' && payload.exp * 1000 > Date.now() + EXPIRY_BUFFER_MS;
}

/**
 * True if the token can be used as the share-link Authorization header for `expectedObjectId`.
 *
 * Real users (`sign_in_provider !== 'anonymous'`) pass once not expired — the backend
 * augments share-link context via the `object-id` HTTP header.
 *
 * Anonymous users must additionally carry `shareLinkObjectId` matching `expectedObjectId`
 * (or any objectId, when `expectedObjectId` is omitted). A claim mismatch means the cache
 * was populated for a different share link OR before postAccessShareLink set the claim.
 */
export function isShareTokenValidForObjectId(token: string, expectedObjectId?: string): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload || !isJwtNotExpired(payload)) return false;
  if (payload.firebase?.sign_in_provider !== 'anonymous') return true;
  if (!payload.shareLinkObjectId) return false;
  if (expectedObjectId && payload.shareLinkObjectId !== expectedObjectId) return false;
  return true;
}

/**
 * Extract the share-link objectId from a `/share/{folder|playlist}/{objectId}` path.
 * Returns null on signIn pages or when not on a share-link page at all.
 */
export function extractShareObjectIdFromPath(pathname: string): string | null {
  const match = pathname.match(/^\/share\/(?:folder|playlist)\/([^/]+)/);
  return match?.[1] ?? null;
}
